import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { TOMBSTONES } from '../../src/game/data.js';
import {
  inspectOneShotSample,
  previewOneShot,
  previewOneShotSample,
} from './proceduralSound.js';
import {
  cloneGlbPreview,
  formatGlbTriangles,
  loadCatalogGlb,
} from './glbImportUtils.js';

const $=(root,selector)=>root.querySelector(selector);
const $$=(root,selector)=>[...root.querySelectorAll(selector)];
const AUTO_ROTATE_STORAGE_KEY='toybox-catalog-studio:tombstone-auto-rotate';
const MARKER_STYLES=new Set(['stone','cone','holo','blocks']);

function cleanId(value){
  return String(value||'lapida-personalizada')
    .normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .replace(/[^a-zA-Z0-9_-]+/g,'-')
    .replace(/^-+|-+$/g,'')
    .toLowerCase()||'lapida-personalizada';
}

function readAutoRotatePreference(){
  try{
    const stored=window.localStorage.getItem(AUTO_ROTATE_STORAGE_KEY);
    return stored===null?true:stored==='true';
  }catch{
    return true;
  }
}

function saveAutoRotatePreference(enabled){
  try{
    window.localStorage.setItem(AUTO_ROTATE_STORAGE_KEY,String(enabled));
  }catch{
    // La vista sigue funcionando aunque el navegador bloquee localStorage.
  }
}

function styleDefaults(style){
  return {
    stone:{baseWidth:.58,baseDepth:.34,baseHeight:.12},
    cone:{baseWidth:.48,baseDepth:.48,baseHeight:.08},
    holo:{baseWidth:.62,baseDepth:.62,baseHeight:.10},
    blocks:{baseWidth:.50,baseDepth:.34,baseHeight:.13},
  }[style];
}

function cloneDesign(source={}){
  const style=MARKER_STYLES.has(source.style)?source.style:'stone';
  const base=styleDefaults(style);
  const color=source.color||'#6f7180';
  return {
    style,
    color,
    accent:source.accent||'#d9dde2',
    baseColor:source.baseColor||(['holo','blocks'].includes(style)?color:'#34313d'),
    scale:Number(source.scale??1),
    metalness:Number(source.metalness??.32),
    roughness:Number(source.roughness??.52),
    baseWidth:Number(source.baseWidth??base.baseWidth),
    baseDepth:Number(source.baseDepth??base.baseDepth),
    baseHeight:Number(source.baseHeight??base.baseHeight),
    slabWidth:Number(source.slabWidth??.48),
    slabHeight:Number(source.slabHeight??.62),
    slabDepth:Number(source.slabDepth??.15),
    slabRoundness:Number(source.slabRoundness??.13),
    plateWidth:Number(source.plateWidth??.30),
    plateHeight:Number(source.plateHeight??.16),
    plateDepth:Number(source.plateDepth??.025),
    plateOffsetY:Number(source.plateOffsetY??.40),
    coneBottomRadius:Number(source.coneBottomRadius??.20),
    coneTopRadius:Number(source.coneTopRadius??.055),
    coneHeight:Number(source.coneHeight??.52),
    stripeRadius:Number(source.stripeRadius??.145),
    stripeHeight:Number(source.stripeHeight??.10),
    stripeOffsetY:Number(source.stripeOffsetY??.29),
    stemHeight:Number(source.stemHeight??.48),
    stemRadius:Number(source.stemRadius??.035),
    ringRadius:Number(source.ringRadius??.22),
    ringThickness:Number(source.ringThickness??.035),
    ringHeight:Number(source.ringHeight??.42),
    glowOpacity:Number(source.glowOpacity??.80),
    uprightWidth:Number(source.uprightWidth??.14),
    uprightHeight:Number(source.uprightHeight??.58),
    uprightDepth:Number(source.uprightDepth??.14),
    crossWidth:Number(source.crossWidth??.46),
    crossHeight:Number(source.crossHeight??.14),
    crossDepth:Number(source.crossDepth??.14),
    crossOffsetY:Number(source.crossOffsetY??.48),
    modelRotationX:Number(source.modelRotationX??0),
    modelRotationY:Number(source.modelRotationY??0),
    modelRotationZ:Number(source.modelRotationZ??0),
    soundWave:source.soundWave||'triangle',
    soundTone:Number(source.soundTone??180),
    soundDuration:Number(source.soundDuration??.55),
    soundFilter:Number(source.soundFilter??780),
    soundNoiseGain:Number(source.soundNoiseGain??.055),
    soundToneGain:Number(source.soundToneGain??.065),
    soundSampleVolume:Number(source.soundSampleVolume??.5),
    soundSampleRate:Number(source.soundSampleRate??1),
  };
}

function disposeObject(object){
  object?.traverse((child)=>{
    if(!child.isMesh)return;
    child.geometry?.dispose();
    const materials=Array.isArray(child.material)?child.material:[child.material];
    materials.filter(Boolean).forEach((material)=>material.dispose?.());
  });
}

function roundedBox(width,height,depth,material,radius=.04){
  return new THREE.Mesh(
    new RoundedBoxGeometry(width,height,depth,3,Math.min(radius,width*.25,height*.25,depth*.25)),
    material,
  );
}

function createMarker(design){
  const root=new THREE.Group();
  root.name='procedural_tombstone_preview';
  root.scale.setScalar(design.scale);
  const mainMaterial=new THREE.MeshPhysicalMaterial({
    color:design.color,
    metalness:design.metalness,
    roughness:design.roughness,
    clearcoat:.28,
  });
  const accentMaterial=new THREE.MeshPhysicalMaterial({
    color:design.accent,
    metalness:Math.max(.1,design.metalness-.08),
    roughness:Math.min(.9,design.roughness+.02),
    clearcoat:.45,
  });
  const baseMaterial=new THREE.MeshStandardMaterial({
    color:design.baseColor,
    metalness:.24,
    roughness:.62,
  });

  if(design.style==='cone'){
    const base=roundedBox(
      design.baseWidth,
      design.baseHeight,
      design.baseDepth,
      baseMaterial,
      .04,
    );
    base.position.y=design.baseHeight*.5;
    const cone=new THREE.Mesh(
      new THREE.CylinderGeometry(
        design.coneTopRadius,
        design.coneBottomRadius,
        design.coneHeight,
        24,
      ),
      mainMaterial,
    );
    cone.position.y=design.baseHeight+design.coneHeight*.5;
    const stripe=new THREE.Mesh(
      new THREE.CylinderGeometry(
        design.stripeRadius*.78,
        design.stripeRadius,
        design.stripeHeight,
        24,
      ),
      accentMaterial,
    );
    stripe.position.y=design.baseHeight+design.stripeOffsetY;
    root.add(base,cone,stripe);
  }else if(design.style==='holo'){
    const base=new THREE.Mesh(
      new THREE.CylinderGeometry(
        design.baseWidth*.42,
        design.baseWidth*.5,
        design.baseHeight,
        28,
      ),
      baseMaterial,
    );
    base.position.y=design.baseHeight*.5;
    base.scale.z=design.baseDepth/design.baseWidth;
    const stem=new THREE.Mesh(
      new THREE.CylinderGeometry(
        design.stemRadius*.72,
        design.stemRadius,
        design.stemHeight,
        14,
      ),
      accentMaterial,
    );
    stem.position.y=design.baseHeight+design.stemHeight*.5;
    const ringMaterial=new THREE.MeshBasicMaterial({
      color:design.accent,
      transparent:true,
      opacity:design.glowOpacity,
    });
    const ring=new THREE.Mesh(
      new THREE.TorusGeometry(design.ringRadius,design.ringThickness,12,40),
      ringMaterial,
    );
    ring.rotation.x=Math.PI/2;
    ring.position.y=design.baseHeight+design.ringHeight;
    root.add(base,stem,ring);
  }else if(design.style==='blocks'){
    const base=roundedBox(
      design.baseWidth,
      design.baseHeight,
      design.baseDepth,
      baseMaterial,
      .035,
    );
    base.position.y=design.baseHeight*.5;
    const upright=roundedBox(
      design.uprightWidth,
      design.uprightHeight,
      design.uprightDepth,
      accentMaterial,
      .018,
    );
    upright.position.y=design.baseHeight+design.uprightHeight*.5;
    const cross=roundedBox(
      design.crossWidth,
      design.crossHeight,
      design.crossDepth,
      mainMaterial,
      .018,
    );
    cross.position.y=design.baseHeight+design.crossOffsetY;
    root.add(base,upright,cross);
  }else{
    const base=roundedBox(
      design.baseWidth,
      design.baseHeight,
      design.baseDepth,
      baseMaterial,
      .045,
    );
    base.position.y=design.baseHeight*.5;
    const slab=roundedBox(
      design.slabWidth,
      design.slabHeight,
      design.slabDepth,
      mainMaterial,
      design.slabRoundness,
    );
    slab.position.y=design.baseHeight+design.slabHeight*.5;
    const plate=roundedBox(
      design.plateWidth,
      design.plateHeight,
      design.plateDepth,
      accentMaterial,
      .035,
    );
    plate.position.set(
      0,
      design.baseHeight+design.plateOffsetY,
      -(design.slabDepth+design.plateDepth)*.5,
    );
    root.add(base,slab,plate);
  }
  root.traverse((child)=>{
    if(!child.isMesh)return;
    child.castShadow=true;
    child.receiveShadow=true;
  });
  return root;
}

export class TombstoneEditor{
  constructor(screen,callbacks={}){
    this.screen=screen;
    this.callbacks=callbacks;
    this.canvas=$(screen,'#tombstone-editor-canvas');
    this.status=$(screen,'#tombstone-editor-message');
    this.saveButton=$(screen,'#tombstone-editor-save');
    this.fileInput=$(screen,'#tombstone-editor-file');
    this.fileName=$(screen,'#tombstone-editor-file-name');
    this.fileInfo=$(screen,'#tombstone-editor-file-info');
    this.soundFileInput=$(screen,'#tombstone-editor-sound-file');
    this.soundFileName=$(screen,'#tombstone-editor-sound-file-name');
    this.soundFileInfo=$(screen,'#tombstone-editor-sound-file-info');
    this.testSoundButton=$(screen,'#tombstone-editor-test-sound');
    this.templateSelect=$(screen,'#tombstone-editor-template');
    this.styleSelect=$(screen,'#tombstone-editor-style');
    this.autoRotateInput=$(screen,'#tombstone-editor-auto-rotate');
    this.autoRotateInput.checked=readAutoRotatePreference();
    this.active=false;
    this.mode='procedural';
    this.assetFile=null;
    this.assetStats=null;
    this.soundMode='procedural';
    this.soundFile=null;
    this.soundStats=null;
    this.design=cloneDesign(TOMBSTONES[0]);
    this.initScene();
    this.bindInterface();
    this.syncControls();
    this.rebuild();
    this.animate();
  }

  initScene(){
    this.renderer=new THREE.WebGLRenderer({
      canvas:this.canvas,
      antialias:true,
      powerPreference:'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio,1.75));
    this.renderer.outputColorSpace=THREE.SRGBColorSpace;
    this.renderer.toneMapping=THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure=1.08;
    this.renderer.shadowMap.enabled=true;

    this.scene=new THREE.Scene();
    this.scene.background=new THREE.Color('#17172a');
    this.scene.fog=new THREE.Fog('#17172a',8,18);
    this.camera=new THREE.PerspectiveCamera(38,1,.03,40);
    this.camera.position.set(3,2.2,-4);
    this.controls=new OrbitControls(this.camera,this.canvas);
    this.controls.enableDamping=true;
    this.controls.dampingFactor=.075;
    this.controls.target.set(0,.38,0);
    this.controls.minDistance=1.8;
    this.controls.maxDistance=8;

    this.scene.add(new THREE.HemisphereLight(0xe8f3ff,0x25213a,2.2));
    const key=new THREE.DirectionalLight(0xffedd3,4.5);
    key.position.set(-4,7,-5);
    key.castShadow=true;
    key.shadow.mapSize.set(1024,1024);
    this.scene.add(key);
    const fill=new THREE.DirectionalLight(0x63cfff,2.2);
    fill.position.set(5,3,4);
    this.scene.add(fill);

    const floor=new THREE.Mesh(
      new THREE.CircleGeometry(2.25,64),
      new THREE.MeshStandardMaterial({color:'#201c34',roughness:.9,metalness:.05}),
    );
    floor.rotation.x=-Math.PI/2;
    floor.receiveShadow=true;
    this.scene.add(floor);
    const grid=new THREE.GridHelper(6,18,0x4dddec,0x3b3551);
    grid.position.y=.005;
    grid.material.transparent=true;
    grid.material.opacity=.28;
    this.scene.add(grid);
    this.previewMount=new THREE.Group();
    this.scene.add(this.previewMount);
  }

  bindInterface(){
    $(this.screen,'#tombstone-editor-close').addEventListener('click',()=>this.close());
    $(this.screen,'#tombstone-editor-reset').addEventListener('click',()=>this.resetDesign());
    this.saveButton.addEventListener('click',()=>this.save());
    this.testSoundButton.addEventListener('click',()=>this.testSound());
    $$ (this.screen,'[data-tombstone-mode]').forEach((button)=>{
      button.addEventListener('click',()=>this.setMode(button.dataset.tombstoneMode));
    });
    $$ (this.screen,'[data-tombstone-sound-mode]').forEach((button)=>{
      button.addEventListener('click',()=>this.setSoundMode(button.dataset.tombstoneSoundMode));
    });
    this.templateSelect.addEventListener('change',()=>{
      const template=TOMBSTONES.find((item)=>item.id===this.templateSelect.value)||TOMBSTONES[0];
      this.design=cloneDesign(template);
      this.syncControls();
      if(this.mode==='procedural')this.rebuild();
    });
    this.styleSelect.addEventListener('change',()=>{
      const nextStyle=this.styleSelect.value;
      const nextBase=styleDefaults(nextStyle);
      this.design=cloneDesign({
        ...this.design,
        style:nextStyle,
        baseWidth:nextBase.baseWidth,
        baseDepth:nextBase.baseDepth,
        baseHeight:nextBase.baseHeight,
      });
      this.syncControls();
      if(this.mode==='procedural')this.rebuild();
    });
    $$ (this.screen,'[data-tombstone-design]').forEach((input)=>{
      input.addEventListener('input',()=>{
        const key=input.dataset.tombstoneDesign;
        this.design[key]=(input.type==='color'||input.tagName==='SELECT')?input.value:Number(input.value);
        this.syncOutput(input);
        if(this.mode==='procedural')this.rebuild();
        else if(key.startsWith('modelRotation'))this.refreshImportedPreview(true);
      });
    });
    this.fileInput.addEventListener('change',()=>this.loadGlbFile(this.fileInput.files[0]));
    this.soundFileInput.addEventListener('change',()=>this.loadSoundFile(this.soundFileInput.files[0]));
    this.autoRotateInput.addEventListener('change',()=>{
      saveAutoRotatePreference(this.autoRotateInput.checked);
    });
    window.addEventListener('resize',()=>this.resize());
  }

  syncOutput(input){
    input.syncNumberControl?.();
    const output=$(this.screen,`[data-tombstone-output="${input.dataset.tombstoneDesign}"]`);
    if(!output)return;
    const value=this.design[input.dataset.tombstoneDesign];
    const decimals=Number(input.step)<.01?3:2;
    output.textContent=Number.isInteger(value)?value:Number(value).toFixed(decimals);
  }

  syncControls(){
    $$ (this.screen,'[data-tombstone-design]').forEach((input)=>{
      input.value=this.design[input.dataset.tombstoneDesign];
      this.syncOutput(input);
    });
    this.styleSelect.value=this.design.style;
    $$ (this.screen,'[data-tombstone-mode]').forEach((button)=>{
      button.classList.toggle('selected',button.dataset.tombstoneMode===this.mode);
    });
    $(this.screen,'[data-tombstone-panel="procedural"]').classList.toggle(
      'hidden',
      this.mode!=='procedural',
    );
    $(this.screen,'[data-tombstone-panel="glb"]').classList.toggle(
      'hidden',
      this.mode!=='glb',
    );
    $$ (this.screen,'[data-tombstone-style-panel]').forEach((panel)=>{
      panel.classList.toggle('hidden',panel.dataset.tombstoneStylePanel!==this.design.style);
    });
    $$ (this.screen,'[data-tombstone-sound-mode]').forEach((button)=>{
      button.classList.toggle('selected',button.dataset.tombstoneSoundMode===this.soundMode);
    });
    $$ (this.screen,'[data-tombstone-sound-panel]').forEach((panel)=>{
      panel.classList.toggle('hidden',panel.dataset.tombstoneSoundPanel!==this.soundMode);
    });
    this.updateActionButtons();
  }

  updateActionButtons(){
    const missingModel=this.mode==='glb'&&!this.assetFile;
    const missingSound=this.soundMode==='mp3'&&!this.soundFile;
    this.saveButton.disabled=missingModel||missingSound;
    this.testSoundButton.disabled=missingSound;
  }

  replacePreview(object){
    if(this.previewObject){
      this.previewMount.remove(this.previewObject);
      disposeObject(this.previewObject);
    }
    this.previewObject=object;
    this.previewMount.add(object);
  }

  createImportedPreview(){
    if(!this.importedPreview)return null;
    const model=cloneGlbPreview(this.importedPreview);
    model.rotation.set(
      THREE.MathUtils.degToRad(this.design.modelRotationX),
      THREE.MathUtils.degToRad(this.design.modelRotationY),
      THREE.MathUtils.degToRad(this.design.modelRotationZ),
    );
    model.updateMatrixWorld(true);
    let bounds=new THREE.Box3().setFromObject(model);
    const size=bounds.getSize(new THREE.Vector3());
    model.scale.multiplyScalar((.92*this.design.scale)/Math.max(size.y,.0001));
    model.updateMatrixWorld(true);
    bounds=new THREE.Box3().setFromObject(model);
    const center=bounds.getCenter(new THREE.Vector3());
    model.position.set(-center.x,-bounds.min.y,-center.z);
    const oriented=new THREE.Group();
    oriented.name='oriented_tombstone_preview';
    oriented.add(model);
    return oriented;
  }

  refreshImportedPreview(showStatus=false){
    const preview=this.createImportedPreview();
    if(!preview)return;
    this.replacePreview(preview);
    if(showStatus){
      this.setStatus(
        `Orientación aplicada: X ${this.design.modelRotationX}° · Y ${this.design.modelRotationY}° · Z ${this.design.modelRotationZ}°.`,
      );
    }
  }

  rebuild(){
    this.replacePreview(createMarker(this.design));
    this.setStatus('Vista procedural actualizada. Los cambios se guardan como parámetros editables.');
  }

  setMode(mode){
    if(mode!=='procedural'&&mode!=='glb')return;
    this.mode=mode;
    this.syncControls();
    if(mode==='procedural'){
      this.rebuild();
    }else if(this.importedPreview){
      this.refreshImportedPreview();
    }else{
      this.setStatus('Elegí una lápida GLB para validarla y previsualizarla.');
    }
  }

  async loadGlbFile(file){
    if(!file)return;
    this.setStatus('Analizando la lápida GLB…');
    try{
      const result=await loadCatalogGlb(file,'tombstone');
      disposeObject(this.importedPreview);
      this.importedPreview=result.preview;
      this.assetFile=file;
      this.assetStats=result.stats;
      this.refreshImportedPreview();
      this.fileName.textContent=file.name;
      const warningText=result.stats.warnings.length?' · revisar':'';
      this.fileInfo.textContent=`${result.stats.meshes} mallas · ${formatGlbTriangles(result.stats.triangles)}${warningText}`;
      this.updateActionButtons();
      this.setStatus(
        result.stats.warnings[0]
          ?`GLB cargado. ${result.stats.warnings.join(' ')}`
          :'GLB validado y normalizado. La base queda apoyada sobre Y=0 y el frente apunta hacia −Z.',
        result.stats.warnings.length?'warning':'ready',
      );
    }catch(error){
      console.error(error);
      this.assetFile=null;
      this.assetStats=null;
      this.fileName.textContent='Elegir archivo GLB';
      this.fileInfo.textContent='No se pudo validar el archivo';
      this.updateActionButtons();
      this.setStatus(error.message||'No se pudo abrir la lápida GLB.','error');
    }
  }

  setSoundMode(mode){
    if(mode!=='procedural'&&mode!=='mp3')return;
    this.soundMode=mode;
    this.syncControls();
    this.setStatus(
      mode==='mp3'
        ?this.soundFile?'MP3 de aparición listo para probar.':'Elegí un MP3 corto para la aparición de la lápida.'
        :'Sonido procedural de aparición listo para probar.',
      mode==='mp3'&&this.soundFile?'success':'ready',
    );
  }

  async loadSoundFile(file){
    if(!file)return;
    this.saveButton.disabled=true;
    this.testSoundButton.disabled=true;
    this.setStatus('Analizando el sonido MP3 de aparición…');
    try{
      const stats=await inspectOneShotSample(file);
      this.soundMode='mp3';
      this.soundFile=file;
      this.soundStats=stats;
      this.soundFileName.textContent=file.name;
      this.soundFileInfo.textContent=[
        `${stats.duration.toFixed(2)} s`,
        `${stats.sampleRate} Hz`,
        stats.channels===1?'mono':`${stats.channels} canales`,
        stats.warnings.length?'revisar duración':'listo',
      ].join(' · ');
      this.syncControls();
      this.setStatus(
        stats.warnings[0]||'MP3 validado. Se reproducirá al aparecer la lápida.',
        stats.warnings.length?'warning':'success',
      );
    }catch(error){
      console.error(error);
      this.soundFile=null;
      this.soundStats=null;
      this.soundFileInput.value='';
      this.soundFileName.textContent='Elegir archivo MP3';
      this.soundFileInfo.textContent='No se pudo validar el archivo';
      this.syncControls();
      this.setStatus(error.message||'No se pudo abrir el MP3.','error');
    }
  }

  async testSound(){
    if(this.soundMode==='mp3'&&!this.soundFile){
      this.setStatus('Cargá un MP3 antes de probar el sonido.','warning');
      return;
    }
    this.testSoundButton.disabled=true;
    try{
      if(this.soundMode==='mp3')await previewOneShotSample(this.soundFile,this.design);
      else previewOneShot(this.design,'sound');
      this.setStatus(
        this.soundMode==='mp3'
          ?'Reproduciendo el MP3 de aparición.'
          :'Reproduciendo el sonido procedural de aparición.',
        'success',
      );
    }catch(error){
      console.error(error);
      this.setStatus(error.message||'No se pudo reproducir el sonido.','error');
    }finally{
      this.updateActionButtons();
    }
  }

  setStatus(message,state='ready'){
    this.status.textContent=message;
    this.status.dataset.state=state;
  }

  resetDesign(){
    const template=TOMBSTONES.find((item)=>item.id===this.templateSelect.value)||TOMBSTONES[0];
    this.design=cloneDesign(template);
    this.syncControls();
    if(this.mode==='procedural')this.rebuild();
    else this.refreshImportedPreview(true);
  }

  clearWorkspace(){
    this.mode='procedural';
    this.assetFile=null;
    this.assetStats=null;
    this.soundMode='procedural';
    this.soundFile=null;
    this.soundStats=null;
    disposeObject(this.importedPreview);
    this.importedPreview=null;
    this.fileInput.value='';
    this.fileName.textContent='Elegir archivo GLB';
    this.fileInfo.textContent='Base sobre Y=0 · frente hacia −Z';
    this.soundFileInput.value='';
    this.soundFileName.textContent='Elegir archivo MP3';
    this.soundFileInfo.textContent='Muestra corta y sin silencio inicial';
    this.templateSelect.value=TOMBSTONES[0].id;
    this.design=cloneDesign(TOMBSTONES[0]);
    this.syncControls();
    this.rebuild();
  }

  open(){
    this.active=true;
    this.callbacks.onOpen?.();
    requestAnimationFrame(()=>this.resize());
  }

  close(){
    this.active=false;
    this.callbacks.onClose?.();
  }

  async loadRecord(item){
    disposeObject(this.importedPreview);
    this.importedPreview=null;
    this.fileInput.value='';
    this.fileName.textContent='Elegir archivo GLB';
    this.fileInfo.textContent='Base sobre Y=0 · frente hacia −Z';
    this.soundFileInput.value='';
    this.soundFileName.textContent='Elegir archivo MP3';
    this.soundFileInfo.textContent='Muestra corta y sin silencio inicial';
    this.mode=item.profile?.mode||'procedural';
    this.design=cloneDesign(item.profile?.design||TOMBSTONES[0]);
    this.assetFile=item.assetFile||null;
    this.assetStats=item.profile?.asset||null;
    this.soundMode=item.profile?.sound?.mode==='mp3'?'mp3':'procedural';
    this.soundFile=item.audioFile||null;
    this.soundStats=item.profile?.sound||null;
    this.syncControls();
    if(this.mode==='glb'&&this.assetFile)await this.loadGlbFile(this.assetFile);
    else this.rebuild();
    if(this.soundMode==='mp3'&&this.soundFile)await this.loadSoundFile(this.soundFile);
    else this.updateActionButtons();
  }

  async save(){
    const id=cleanId($(this.screen,'#tombstone-editor-id').value);
    $(this.screen,'#tombstone-editor-id').value=id;
    if(this.mode==='glb'&&!this.assetFile){
      this.setStatus('Cargá un GLB antes de guardar una lápida importada.','warning');
      return;
    }
    if(this.soundMode==='mp3'&&!this.soundFile){
      this.setStatus('Cargá un MP3 antes de guardar el sonido importado.','warning');
      return;
    }
    this.saveButton.disabled=true;
    try{
      const result=await this.callbacks.onSave?.({
        id,
        mode:this.mode,
        design:cloneDesign(this.design),
        assetFile:this.assetFile,
        assetStats:this.assetStats,
        soundMode:this.soundMode,
        soundFile:this.soundFile,
        soundStats:this.soundStats,
      });
      this.setStatus(result?.message||'Lápida guardada.','success');
    }catch(error){
      console.error(error);
      this.setStatus(error.message||'No se pudo guardar la lápida.','error');
    }finally{
      this.updateActionButtons();
    }
  }

  resize(){
    if(!this.active)return;
    const width=this.canvas.clientWidth;
    const height=this.canvas.clientHeight;
    if(!width||!height)return;
    this.renderer.setSize(width,height,false);
    this.camera.aspect=width/height;
    this.camera.updateProjectionMatrix();
  }

  animate(){
    requestAnimationFrame(()=>this.animate());
    if(!this.active)return;
    this.resize();
    this.controls.update();
    if(this.autoRotateInput.checked)this.previewMount.rotation.y+=.0025;
    this.renderer.render(this.scene,this.camera);
  }
}
